import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  type App,
  appService,
  Button,
  Input,
  LanguageSwitcher,
  logOut,
  useAuth,
  workspaceService,
} from '@aaas/shared';
import { Check, Loader2, LogOut, QrCode } from 'lucide-react';

const slugHue = (slug: string): number => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
};

function AppIcon({ slug, name }: { slug: string; name: string }) {
  const hue = slugHue(slug);
  const initial = (name || slug).trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-base font-semibold text-white"
      style={{ backgroundColor: `hsl(${hue}, 55%, 50%)` }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

export function WelcomePage() {
  const navigate = useNavigate();
  const {
    user,
    userProfile,
    refreshWorkspaces,
    switchWorkspace,
    availableWorkspaces,
    workspaceLoading,
  } = useAuth();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());

  const [catalog, setCatalog] = useState<App[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // /welcome is reserved for the empty-state onboarding flow. If the user
  // already has at least one workspace, bounce them back to /.
  const shouldRedirect =
    !workspaceLoading && availableWorkspaces.length > 0;

  // Greeting label
  const greetingName =
    userProfile?.display_name || userProfile?.email?.split('@')[0] || 'bạn';

  // Load app catalog
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await user.getIdToken();
        const apps = await appService.getAppCatalog(idToken);
        if (!cancelled) {
          setCatalog(apps.filter((a) => a.is_active));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load app catalog.',
          );
        }
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Debounce code availability check
  useEffect(() => {
    if (!code || code.length < 3) {
      setCodeAvailable(null);
      return;
    }
    setCheckingCode(true);
    const handle = setTimeout(async () => {
      try {
        const result = await workspaceService.checkWorkspaceCode(code);
        setCodeAvailable(result.available);
      } catch {
        setCodeAvailable(null);
      } finally {
        setCheckingCode(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [code]);

  const handleCodeChange = (value: string) => {
    setCode(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const toggleApp = (appId: string) => {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleSignOut = async () => {
    try {
      await logOut();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    navigate('/login', { replace: true });
  };

  const canSubmit =
    !!name.trim() &&
    code.length >= 3 &&
    codeAvailable === true &&
    !checkingCode &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const idToken = await user.getIdToken();
      const workspace = await workspaceService.createTeamWorkspace(
        { code: code.trim(), name: name.trim() },
        idToken,
      );

      // Switch active workspace BEFORE subscribing — the subscribe endpoint
      // resolves the workspace via the X-Workspace-ID header that the axios
      // interceptor reads from localStorage. Without this, every subscribe
      // call hits the API without the header and gets rejected as 400.
      await switchWorkspace(workspace.id);

      // Subscribe to chosen apps in parallel. Per-app failures are surfaced
      // but do not roll back the workspace — user can subscribe later.
      const results = await Promise.allSettled(
        Array.from(selectedApps).map((appId) =>
          appService.subscribeWorkspaceToApp(workspace.id, appId, idToken),
        ),
      );
      const failed = results
        .map((r, i) => (r.status === 'rejected' ? { i, r } : null))
        .filter((x): x is { i: number; r: PromiseRejectedResult } => x !== null);
      if (failed.length > 0) {
        console.warn('Some app subscriptions failed', failed);
        const failedNames = failed
          .map(({ i }) => {
            const id = Array.from(selectedApps)[i];
            return catalog.find((a) => a.id === id)?.name ?? id;
          })
          .join(', ');
        setError(
          `Workspace đã tạo, nhưng ${failed.length} ứng dụng không đăng ký được: ${failedNames}. Bạn có thể đăng ký lại sau.`,
        );
      }

      await refreshWorkspaces();
      navigate('/', { replace: true });
    } catch (err: unknown) {
      console.error('Workspace creation failed', err);
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosErr = err as {
          response?: { data?: { detail?: string; message?: string } };
        };
        setError(
          axiosErr.response?.data?.message ||
            axiosErr.response?.data?.detail ||
            'Tạo workspace thất bại. Vui lòng thử lại.',
        );
      } else {
        setError('Tạo workspace thất bại. Vui lòng thử lại.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (shouldRedirect) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top-right controls */}
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LanguageSwitcher />
        <button
          type="button"
          onClick={handleSignOut}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-12">
        {/* Greeting */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Xin chào, {greetingName} 👋
          </h1>
          <p className="mt-3 text-gray-600">
            Để bắt đầu sử dụng phần mềm, mời bạn tạo Workspace đầu tiên.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Hoặc nếu bạn được mời tham gia workspace có sẵn, hãy{' '}
            <span className="inline-flex items-center gap-1 font-medium text-gray-700">
              <QrCode className="h-3.5 w-3.5" />
              quét mã QR
            </span>{' '}
            do quản trị viên gửi.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
        >
          {/* Workspace name */}
          <div>
            <label
              htmlFor="ws-name"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Tên workspace
            </label>
            <Input
              id="ws-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ví dụ: Công ty ABC"
              maxLength={100}
              required
              disabled={submitting}
            />
          </div>

          {/* Workspace code */}
          <div>
            <label
              htmlFor="ws-code"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Mã workspace
            </label>
            <Input
              id="ws-code"
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="abc-corp"
              minLength={3}
              maxLength={50}
              required
              disabled={submitting}
              className={
                codeAvailable === true
                  ? 'border-green-500 focus-visible:ring-green-500'
                  : codeAvailable === false
                    ? 'border-red-500 focus-visible:ring-red-500'
                    : ''
              }
            />
            <div className="mt-1 min-h-[20px] text-xs">
              {code.length > 0 && code.length < 3 && (
                <span className="text-gray-500">
                  Mã phải có ít nhất 3 ký tự (chữ thường, số, dấu gạch).
                </span>
              )}
              {checkingCode && (
                <span className="text-yellow-600">Đang kiểm tra…</span>
              )}
              {!checkingCode && codeAvailable === true && code.length >= 3 && (
                <span className="text-green-600">Mã khả dụng ✓</span>
              )}
              {!checkingCode && codeAvailable === false && (
                <span className="text-red-600">Mã đã được dùng</span>
              )}
            </div>
          </div>

          {/* App picker */}
          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Chọn ứng dụng muốn dùng
              </label>
              <span className="text-xs text-gray-500">
                Đã chọn {selectedApps.size}
                {catalog.length ? `/${catalog.length}` : ''}
              </span>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Workspace sẽ được đăng ký các ứng dụng bạn chọn. Bạn có thể đăng
              ký hoặc hủy đăng ký thêm sau này.
            </p>
            {loadingCatalog ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Đang tải danh sách ứng dụng…
              </div>
            ) : catalog.length === 0 ? (
              <p className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
                Chưa có ứng dụng nào trên hệ thống.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {catalog.map((app) => {
                  const selected = selectedApps.has(app.id);
                  return (
                    <button
                      type="button"
                      key={app.id}
                      onClick={() => toggleApp(app.id)}
                      disabled={submitting}
                      className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-50 ${
                        selected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <AppIcon slug={app.slug} name={app.name} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold text-gray-900">
                            {app.name}
                          </span>
                          {app.is_beta && (
                            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] text-orange-700">
                              Beta
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">
                          {app.description}
                        </p>
                      </div>
                      <div
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border ${
                          selected
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <Button type="submit" disabled={!canSubmit} className="w-full">
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Đang tạo workspace…
              </>
            ) : (
              'Tạo workspace'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
